import React, { useEffect, useState } from 'react';

import { Logo } from '../Logo';

import type { AvatarProps, AvatarSize, AvatarTone } from './Avatar.types';

/** Named diameter → px. Mirrors `components.avatar.sizes`. */
const SIZE: Record<AvatarSize, number> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 48,
  xl: 64,
  '2xl': 96,
};

/** Tone → the fallback circle's fill and ink. Unused when an image loads. */
const TONE: Record<AvatarTone, string> = {
  neutral: 'bg-contrast-600 text-neutral-300',
  raised: 'bg-contrast-500 text-neutral-300',
  accent: 'bg-sakura-shade text-primary-300',
};

/** The `bloom` ring's thickness between gradient edge and inner circle (`p-1`). */
const RING_PADDING = 4;

function resolveDiameter(size: AvatarSize | number): number {
  return typeof size === 'number' ? size : SIZE[size];
}

/**
 * Avatar - a person as a circle: their photo, or the Dō mark.
 *
 * Every user has a photo. The backend derives a robohash URL from a hash of
 * their email, so there is no "user without a picture" state to design for —
 * which is why this takes no initials. The circle falls back to the 道 glyph
 * only when the avatar service is unreachable, the image 404s, or a response
 * predates the field. All three are the same event to a reader: we could not
 * show you this person, so we show you the app instead.
 *
 * The glyph is the real brand vector via `Logo mask="none"`, not a `dō` text
 * node, so it stays optically identical to the mark everywhere else and needs
 * no CJK webfont.
 *
 * `variant="bloom"` supplies its own fill and ink (the profile page's gradient
 * ring), so `tone` does not apply to it.
 *
 * @example
 * <Avatar src={user.avatar} name="Ada Lovelace" size="sm" />
 * <Avatar src={p.avatar_url} name={p.username} size="xs" showTooltip />  // in a stack
 * <Avatar src={profile.avatar} name={profile.name} variant="bloom" size="2xl" />
 */
export function Avatar({
  src,
  name,
  size = 'sm',
  tone = 'neutral',
  variant = 'default',
  showTooltip = false,
  alt,
  loading,
  className = '',
  'data-testid': testId,
}: AvatarProps): React.JSX.Element {
  const [failed, setFailed] = useState(false);

  // A new src deserves its own attempt — otherwise one broken URL keeps the
  // fallback showing for whoever is rendered into this slot next.
  useEffect(() => setFailed(false), [src]);

  const diameter = resolveDiameter(size);
  const showImage = Boolean(src) && !failed;
  const decorative = alt === undefined;
  // The circle is the only thing naming the person in a stack; everywhere else
  // the name is already printed beside it.
  const tooltip = showTooltip && name ? name : undefined;

  const box = { width: diameter, height: diameter };

  const image = (
    <img
      src={src ?? undefined}
      alt={decorative ? '' : alt}
      loading={loading}
      className="h-full w-full object-cover"
      onError={() => setFailed(true)}
    />
  );

  if (variant === 'bloom') {
    return (
      <div
        className={`rounded-full bg-gradient-bloom p-1 shadow-sakura-glow ${className}`.trim()}
        style={box}
        title={tooltip}
        aria-hidden={decorative || undefined}
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : alt}
        data-testid={testId}
      >
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-contrast-600 text-primary-300">
          {showImage ? (
            image
          ) : (
            <Logo
              size={diameter - RING_PADDING * 2}
              mask="none"
              glyphFill="currentColor"
              decorative
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full ${
        showImage ? '' : TONE[tone]
      } ${className}`
        .replace(/\s+/g, ' ')
        .trim()}
      style={box}
      title={tooltip}
      aria-hidden={decorative || undefined}
      role={!decorative && !showImage ? 'img' : undefined}
      aria-label={!decorative && !showImage ? alt : undefined}
      data-testid={testId}
    >
      {showImage ? (
        image
      ) : (
        <Logo size={diameter} mask="none" glyphFill="currentColor" decorative />
      )}
    </span>
  );
}

export default Avatar;
